import { landmineNames } from '@mm/constants/landmine-names';
import { ArrowStatefulIcon } from '@mm/icons';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import React, { ReactNode, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getPlanInfo } from '@mm/state/helpers/billing-info-helpers';
import {
  EnhancedBillingPage,
  PageBillingType,
} from '@mm/state/interfaces/connected-page';
import ChatbotModel from '@mm/state/model/ChatbotModel';
import CompanyModel from '@mm/state/model/CompanyModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { tourOpenSelector } from '@mm/state/selectors/modalSelector';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import {
  activeChatbotSelector,
  activeCompanySelector,
  selectActiveChatbot,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import noImage from 'assets/icons/contact_noimage.svg';
import { trackWebAppGAEvent } from '../../../common/helpers/plugins/website-google-analytics';
import { useActivateSmsRedirectCallback } from '../../../common/hooks/use-activate-sms-redirect-callback';
import { UserRoleContainer } from '../../../common/hooks/use-route-guard';
import { colors } from '../../../editor/features/beta/styles/colors';
import { BotBuilderRoutes } from '../../../routes/bot-builder-routes';
import { FreeTrialCountdown } from '../FreeTrialCountdown/FreeTrialCountdown';
import { Logo } from '../Logo/Logo';
import SendLimitAlert from './SendLimitAlert/SendLimitAlert';
import './SidebarHeader.pcss';
import { UpgradeRibbon } from './UpgradeRibbon/UpgradeRibbon';
import { ChatbotDropdown } from './ChatbotDropdown/ChatbotDropdown';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { getCurrentSubscription } from '@mm/state/selectors/subscriptionSelectors';
import { AllFeaturesDropdown } from './AllFeaturesDropdown/AllFeaturesDropdown';
import { SidebarMenuIconActions } from './SidebarMenuIconActions/SidebarMenuIconActions';
import { getIsAutomationFeatureEnabled } from '@mm/state/selectors/automations';
import { AutomationTemplateContainer } from '../../../editor/features/beta/modules/Automations/AutomationHome/TemplateIndex/AutomationTemplateContainer';
import { TemplateGoButton } from '../../../editor/features/beta/modules/Automations/AutomationHome/TemplateIndex/TemplateGoButton';
import { PlusOutlined } from '@ant-design/icons';

type Props = {
  children: ReactNode;
  facebookPage: EnhancedBillingPage;
  onMenuClick: () => void;
  setSidebarMinimized: (isMinimized: boolean) => void;
  sidebarMinimized: boolean;
  company: CompanyModel;
  activeChatbot: ChatbotModel | null;
};

const noop = () => {};
export const useUpgradeLandmineHandler = () => {
  const activeChatbot = useSelector(selectActiveChatbot);
  const session = useSelector(selectSessionSelector);
  const activeCompany = useSelector(activeCompanySelector);
  const justCompletedOnboarding = useSelector(
    (state: StoreState) => state.onboarding.justCompletedOnboarding,
  );
  const isNowOnboarding = useSelector(tourOpenSelector);

  const usersCRUDActions = useUsersCRUDActions();
  const modalActions = useModalActions();

  const facebookPage = useSelector(activeFacebookPageSelector);

  const subscription = useSelector(getCurrentSubscription(facebookPage?.id));

  const currentSubscription = subscription || facebookPage?.subscription;

  const shouldShow = useMemo(() => {
    if (activeCompany && activeChatbot && session && session.userAttributes) {
      const isCompanyAdmin = activeCompany?.editable;
      const { seenLastPromo } = session.userAttributes;

      return (
        !currentSubscription &&
        isCompanyAdmin &&
        !seenLastPromo &&
        !justCompletedOnboarding &&
        !isNowOnboarding
      );
    }

    return false;
  }, [
    session,
    justCompletedOnboarding,
    activeCompany,
    activeChatbot,
    isNowOnboarding,
  ]);

  const onSuccessBilling = useActivateSmsRedirectCallback();

  const callback = useCallback(() => {
    if (shouldShow && activeCompany) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['PROMOTIONS__PRO-UPGRADE'],
        planFallback: PageBillingType.Pro,
        onSuccessBilling,
      });
      usersCRUDActions.updateUserAttributes(activeCompany.id, session.id, {
        seenLastPromo: true,
        // HACK do not remove this prop
        // DOING SO WILL BREAK THE WORDPRESS PLUGIN INTEGRATION
        // AS IT RELIES ON IT BEING SET
        // first introduced in FetchBotData L:106 7165ab0ace36e449e570c2831bbefbab44787ecb
        seenNewDashboardModal: true,
      });
    }
  }, [shouldShow, activeCompany]);

  return shouldShow ? callback : noop;
};

const SidebarHeader = (props: Props) => {
  const { facebookPage, onMenuClick, company, activeChatbot, children } = props;
  const planInfo = getPlanInfo(facebookPage);
  const chatbot = useSelector(activeChatbotSelector);
  const isAutomationsEnabled = useSelector(getIsAutomationFeatureEnabled);

  const { roleFlags } = UserRoleContainer.useContainer();
  const showUpgradeRibbon =
    !facebookPage?.redemption?.freeTrial &&
    (planInfo.isFreePlan || planInfo.isProPlan);
  const hideSendLimitAlert = planInfo.isCustomTeam || planInfo.isLegacyTeamPlan;

  const {
    roleFlags: { isLiveChatAgent },
  } = UserRoleContainer.useContainer();

  let logoHref = '';

  if (chatbot?.id && !isLiveChatAgent) {
    logoHref = BotBuilderRoutes.dashboard.getLink({
      chatbotId: chatbot.id,
    });
  }

  const onLogoClick = () => {
    trackWebAppGAEvent('click', {
      category: 'navigation',
      label: 'navbar_logo',
    });
  };

  const showUpgradeLandmine = useUpgradeLandmineHandler();

  return (
    <div className="sidebar-header" onClickCapture={showUpgradeLandmine}>
      <div className="sidebar-header__logo-wrapper">
        <Logo href={logoHref} onClick={onLogoClick} />
      </div>

      <div className="sidebar-header__divider" />

      {children}

      <FreeTrialCountdown />

      <div className="active-page-dropdown" onClick={onMenuClick}>
        <div className="active-page-dropdown__wrapper">
          {/*Fix for white lines on edges caused by browser anti-aliasing if img*/}
          {/*tag used*/}
          <div
            className="active-page-dropdown__page-image"
            style={{
              backgroundImage: `url("${
                facebookPage.squarePicture || noImage
              }")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              border: facebookPage.squarePicture
                ? `1px solid ${colors.palePurple}`
                : 'none',
            }}
          />
          <div className="active-page-dropdown__right-section">
            <div className="active-page-dropdown__page-name">
              <div className="active-page-dropdown__page-name-title">
                {facebookPage.name}
              </div>
            </div>
          </div>

          <ArrowStatefulIcon
            size={12}
            className="active-page-dropdown__arrow"
            direction="left"
            color={colors.accentBlue}
          />
        </div>

        {!hideSendLimitAlert && <SendLimitAlert />}
      </div>

      {isAutomationsEnabled && (
        <>
          <AutomationTemplateContainer.Provider>
            <TemplateGoButton
              label="Automation"
              leftIcon={
                <PlusOutlined className="mobile-header__automation-button-icon" />
              }
              css={{
                button: 'mobile-header__automation-button',
              }}
            />
          </AutomationTemplateContainer.Provider>
          <AllFeaturesDropdown />
        </>
      )}

      <ChatbotDropdown />

      {!isAutomationsEnabled && (
        <>
          <div className="sidebar-header__divider" />
          {!roleFlags.isLiveChatAgent && <SidebarMenuIconActions />}
        </>
      )}

      {!roleFlags.isLiveChatAgent && showUpgradeRibbon && (
        <UpgradeRibbon facebookPage={facebookPage} company={company} />
      )}
    </div>
  );
};

export default SidebarHeader;
